/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import isChromatic from 'chromatic/isChromatic';

import { Button } from '@motorway/mw-highway-code';

import { resolveActions } from 'Components/pages/VehiclePayment/VehiclePayment.helpers';
import { SNOWPLOW_EVENTS } from 'Services/analytics/analytics.const';
import { motorwayPayPageEvents } from 'Services/analytics/events/paymentEvents/motorwayPayPageEvents';
import { sendBrazeMotorwayPayCTAEvent, sendBrazeRequestCallMotorwayPayEvent } from 'Services/braze/brazeEvents';
import useKycPermission from 'Utilities/hooks/useKycPermission';

import type { BenefitsSectionProps } from './BenefitsSection/BenefitsSection';
import type { FAQSectionProps } from './FAQSection/FAQSection';
import type { HeroSectionProps } from './HeroSection/HeroSection';
import { getDiscoverMotorwayPayButtonParams } from './HeroSection/HeroSection.helpers';
import type { ModalJoinMotorwayPayProps } from './ModalJoinMotorwayPay/ModalJoinMotorwayPay';
import { useModalJoinMotorwayPay } from './ModalJoinMotorwayPay/useModalJoinMotorwayPay';
import type { ModalRequestCallbackProps } from './ModalRequestCallback/ModalRequestCallback.types';
import { useRequestCallback } from './ModalRequestCallback/useRequestCallback';
import { OPEN_JOIN_MOTORWAY_PAY_MODAL_HASH, OPEN_REQUEST_CALLBACK_MODAL_HASH } from './DiscoverMWPay.consts';
import type { DiscoverMWPayProps } from './DiscoverMWPay.types';
import { DiscoverMWPayEmitter } from './DiscoverMWPayEmitter';

const REGISTER_YOUR_INTEREST_FORM = 'https://motorway.typeform.com/to/WYFqwDkq';

export const useStickyFooterObserver = ({
	heroRef,
	isPaymentsEnrolled,
}: {
	heroRef: React.RefObject<HTMLDivElement>;
	isPaymentsEnrolled: boolean;
}) => {
	useEffect(() => {
		if (isPaymentsEnrolled) {
			return undefined;
		}

		if (!('IntersectionObserver' in globalThis)) {
			return undefined;
		}

		/**
		 * Don't show sticky footer in Chromatic, as this messes with visual regression tests
		 */
		if (isChromatic()) {
			return undefined;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				DiscoverMWPayEmitter.emit('showSticky', !entry.isIntersecting);
			},
			{
				root: null,
				rootMargin: '0px',
				threshold: 0,
			},
		);

		if (heroRef.current) {
			observer.observe(heroRef.current);
		}
		return () => {
			observer?.disconnect();
		};
	}, [heroRef, isPaymentsEnrolled]);
};

export const useAutoOpenModal = ({
	isLoggedOut,
	isPaymentsEnrolled,
	openJoinModal,
	openRequestCallbackModal,
}: {
	isLoggedOut: boolean;
	isPaymentsEnrolled: boolean;
	openJoinModal?: () => void;
	openRequestCallbackModal?: () => void;
}) => {
	useEffect(() => {
		if (isLoggedOut || isPaymentsEnrolled) {
			window.location.hash = '';

			return undefined;
		}

		const { hash } = window.location;
		const hashStr = hash.substring(1);

		if (hashStr === OPEN_JOIN_MOTORWAY_PAY_MODAL_HASH) {
			openJoinModal?.();
		} else if (hashStr === OPEN_REQUEST_CALLBACK_MODAL_HASH) {
			openRequestCallbackModal?.();
		}

		window.location.hash = '';

		return () => {
			window.location.hash = '';
		};
	}, [isLoggedOut, isPaymentsEnrolled, openJoinModal, openRequestCallbackModal]);
};

export const useDiscoverMWPay = ({ displayAs, requestCallbackCallback, withValidatePhone }: DiscoverMWPayProps) => {
	const hasKycPermission = useKycPermission();
	const heroRef = useRef<HTMLDivElement | null>(null);
	const isLoggedOut = displayAs === 'signedOut';
	const isPaymentsEnrolled = displayAs === 'paymentsDealer';

	const {
		closeModal: closeModalRequestCallback,
		onSubmit: onRequestCallbackSubmit,
		openModal: openRequestCallbackModal,
		state: requestCallbackModalState,
		user,
	} = useRequestCallback({ callback: requestCallbackCallback, withValidatePhone });

	const { closeModal: closeJoinModal, openModal: openJoinModal, state: joinModalState } = useModalJoinMotorwayPay();

	useStickyFooterObserver({ heroRef, isPaymentsEnrolled });

	useAutoOpenModal({ isLoggedOut, isPaymentsEnrolled, openJoinModal, openRequestCallbackModal });

	const [hasStickyButtons, setHasStickyButtons] = useState<boolean>(false);

	useEffect(() => {
		DiscoverMWPayEmitter.on('showSticky', setHasStickyButtons);

		return () => {
			DiscoverMWPayEmitter.off('showSticky', setHasStickyButtons);
		};
	}, []);

	const joinMotorwayButtonProps = getDiscoverMotorwayPayButtonParams({
		hash: OPEN_JOIN_MOTORWAY_PAY_MODAL_HASH,
		isLoggedOut,
		onClick: () => {
			motorwayPayPageEvents<{
				label: string | number;
			}>({
				customData: {
					label: hasStickyButtons ? 'sticky_footer' : 'hero_section',
				},
				name: `Join Motorway Pay button clicked${isLoggedOut ? ' logout' : ''}`,
				schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
				version: SNOWPLOW_EVENTS.CLICK.VERSION,
			});

			if (!isLoggedOut) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				sendBrazeMotorwayPayCTAEvent();
				openJoinModal?.();
			}
		},
	});

	const requestCallbackButtonProps = getDiscoverMotorwayPayButtonParams({
		external: REGISTER_YOUR_INTEREST_FORM,
		isLoggedOut,
		onClick: () => {
			motorwayPayPageEvents<{
				label: string | number;
			}>({
				customData: {
					label: hasStickyButtons ? 'sticky_footer' : 'hero_section',
				},
				name: `Request a call back button clicked${isLoggedOut ? ' logout' : ''}`,
				schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
				version: SNOWPLOW_EVENTS.CLICK.VERSION,
			});

			if (!isLoggedOut) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				sendBrazeRequestCallMotorwayPayEvent();
				openRequestCallbackModal?.();
			}
		},
	});

	const hideStartApplication = !isLoggedOut && !hasKycPermission;

	const actions = resolveActions([
		<Button key="requestCallback" label="Register your interest" variant="primary" {...requestCallbackButtonProps} />,
		hideStartApplication ? null : (
			<Button key="startApplication" label="Start application" variant="secondary" {...joinMotorwayButtonProps} />
		),
	]);

	const heroProps: HeroSectionProps = {
		actions,
		hasStickyButtons,
		isPaymentsEnrolled,
		ref: heroRef,
	};

	const faqProps: FAQSectionProps = {
		isLoggedOut,
	};

	const benefitsProps: BenefitsSectionProps = {
		isLoggedOut,
	};

	const joinModalProps: ModalJoinMotorwayPayProps = {
		dismissModal: closeJoinModal,
	};

	const requestCallbackProps: ModalRequestCallbackProps = {
		defaultValues: user,
		onDismiss: closeModalRequestCallback,
		onSubmit: onRequestCallbackSubmit,
		status: requestCallbackModalState,
	};

	return {
		benefitsProps,
		faqProps,
		heroProps,
		joinModalProps,
		requestCallbackProps,
		showJoinModal: joinModalState !== 'closed',
		showRequestCallbackModal: requestCallbackModalState !== 'closed',
	};
};
